/* eslint-disable import/no-unresolved */
import 'swiper/css';

import type { ComponentProps } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import cx from 'clsx';
import { useRouter } from 'next/router';
import { FreeMode, Lazy } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';

import { useUpdateRouterQuery } from '~/hooks/url';
import type { OAISchema } from '~/types/openapi';

import { useTopPrCategory } from '../../products/queries/useTopPvCategory';

import styles from './PopularCategoryList.module.css';

type Props = {
  className?: string;
  onClickCategory?: (
    category: {
      categoryName: string;
      categoryId: number;
    },
    index: number,
  ) => void;
} & ComponentProps<typeof Swiper>;

const PopularCategoryList = ({
  onClickCategory,
  className,
  ...props
}: Props) => {
  const { query } = useRouter();
  const { data } = useTopPrCategory();
  const updateRouterQuery = useUpdateRouterQuery();
  const swiperRef = useRef<SwiperClass>();

  const activeIndex = useMemo(() => {
    if (!query.categoryId) {
      return 0;
    }
    const targetIndex = data.findIndex(
      (category) => category.categoryId === Number(query.categoryId),
    );
    return targetIndex;
  }, [query, data]);

  useEffect(() => {
    swiperRef.current?.slideTo(activeIndex);
  }, [activeIndex]);

  const onClickCategoryTab = (
    { categoryName, categoryId }: OAISchema['TopPvProductsCategoryDto'],
    index: number,
  ) => {
    void updateRouterQuery({ categoryId: `${categoryId}` });

    onClickCategory?.({ categoryName, categoryId }, index);
  };

  return (
    <Swiper
      lazy
      slidesPerView="auto"
      modules={[Lazy, FreeMode]}
      centeredSlides
      centeredSlidesBounds
      onInit={(ref) => {
        swiperRef.current = ref;
      }}
      className={cx([styles.swiper, className])}
      {...props}
    >
      {data.map((category, index) => {
        return (
          <SwiperSlide
            className={cx([
              styles.slide,
              activeIndex === index && styles.active,
            ])}
            key={category.categoryId}
            onClick={() => onClickCategoryTab(category, index)}
          >
            {category.categoryName}
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
};

export default PopularCategoryList;
